import { PersonalDetailsFormState } from '~/components/profile/personal-details-form';
import { ProductDetailsFormState } from '~/components/profile/product-details-form';
import { LotTypes, UnitTypes, profileErrorCodes } from '~/constants';
import { Locale } from '~/locale/types';
import {
    Products,
    TIdType,
    PersonalDetails,
    LicencePlateInquiryResponse,
    LicencePlateStatus,
} from '~/models/profile';
import { TPCAddressInfo } from '~/store/types';
import { unFormat } from '~/utils/helpers';
import { urlWithBasePath } from '~/utils/url-helpers';

export const capitalizeWordBeginnings = (input: string) => {
    if (!input) return input;
    return input.replace(/(^|\s)\S/g, match => match.toUpperCase());
};

export const mapPersonalDetailsToFormState = (
    personalDetails?: PersonalDetails
): PersonalDetailsFormState => {
    const dob = personalDetails?.dateOfBirth ? new Date(personalDetails.dateOfBirth) : undefined;
    return {
        firstName: personalDetails?.firstName || '',
        middleName: personalDetails?.middleName || '',
        lastName: personalDetails?.lastName || '',
        dateOfBirthDay: dob?.getDate(),
        dateOfBirthMonth: dob && dob.getMonth() + 1,
        dateOfBirthYear: dob?.getFullYear(),
        primaryPhoneNumber: personalDetails?.profilePhoneDetails?.phoneNumber || '',
        primaryPhoneExtensionNumber: personalDetails?.profilePhoneDetails?.extension || '',
    };
};

export const mapPersonalDetailsFormStateToPayload = (
    formData: PersonalDetailsFormState,
    agreedToTermsOfUseOn?: string
) => {
    const { firstName, lastName, middleName } = formData;

    return {
        firstName: capitalizeWordBeginnings(firstName?.trim() as string),
        lastName: capitalizeWordBeginnings(lastName?.trim() as string),
        middleName: capitalizeWordBeginnings(middleName?.trim() as string) || null,
        dateOfBirth:
            formData.dateOfBirthYear && formData.dateOfBirthMonth && formData.dateOfBirthDay
                ? new Date(
                      formData.dateOfBirthYear as number,
                      (formData.dateOfBirthMonth as number) - 1,
                      formData.dateOfBirthDay as number
                  ).toISOString()
                : undefined,
        profilePhoneDetails: {
            phoneNumber: unFormat(formData.primaryPhoneNumber?.trim()) || undefined,
            extension: formData.primaryPhoneExtensionNumber?.trim() || undefined,
        },
        agreedToTermsOfUseOn,
    };
};

export const mapToProductFormValue = (
    products?: Products,
    idType?: TIdType,
    details?: PersonalDetails,
    mode?: 'ADD' | 'EDIT'
): ProductDetailsFormState | undefined => {
    if (!products) {
        return undefined;
    }

    switch (idType) {
        case 'DriversLicense':
            return mode === 'EDIT'
                ? {
                      postalCode: products.driverLicense?.postalCode,
                      driversLicenseNumber: products.driverLicense?.number,
                  }
                : {};
        case 'PhotoCard':
            return mode === 'EDIT'
                ? {
                      postalCode: products.photoCard?.postalCode,
                      photoCardNumber: products.photoCard?.number,
                  }
                : {};
        case 'HealthCard': {
            const dob = products.healthCard?.dateOfBirth
                ? new Date(products.healthCard?.dateOfBirth)
                : details?.dateOfBirth
                  ? new Date(details?.dateOfBirth)
                  : undefined;
            const dobYear = dob?.getFullYear();
            const dobMonth = dob ? dob.getMonth() + 1 : undefined;
            const dobDay = dob ? dob.getDate() : undefined;

            const partialResult = {
                dateOfBirthYear: dobYear,
                dateOfBirthMonth: dobMonth,
                dateOfBirthDay: dobDay,
            };

            return mode === 'EDIT'
                ? {
                      ...partialResult,
                      postalCode: products.healthCard?.postalCode,
                      healthCardNumber: products.healthCard?.number,
                      healthCardDocumentControlNumber: products.healthCard?.controlNumber,
                  }
                : partialResult;
        }
        case 'LicensePlate':
            return {
                driversLicenseNumber: products?.driverLicense?.number,
            };
        default:
            return undefined;
    }
};

export const mapToProducts = (formValue: ProductDetailsFormState) => {
    if (!formValue) {
        return undefined;
    }

    return {
        ...(formValue.driversLicenseNumber && {
            driverLicense: {
                number: formValue.driversLicenseNumber,
                trilliumNumber: formValue.driversLicenseTrilliumNumber,
                postalCode: formValue.postalCode,
            },
        }),
        ...(formValue.photoCardNumber && {
            photoCard: {
                number: formValue.photoCardNumber,
                ddNumber: formValue.photoCardDDNumber,
                postalCode: formValue.postalCode,
            },
        }),
        ...(formValue.licensePlateNumber && {
            licencePlates: [
                formValue.hasDriversLicense === 'yes'
                    ? {
                          number: formValue.licensePlateNumber,
                          permitNumber: formValue.permitNumber,
                          driverLicenseNumber: formValue.driversLicenseNumber,
                      }
                    : {
                          number: formValue.licensePlateNumber,
                          permitNumber: formValue.permitNumber,
                          registrantIdentificationNumber: formValue.rin,
                      },
            ],
        }),
        ...(formValue.healthCardNumber && {
            healthCard: {
                number: formValue.healthCardNumber
                    .slice(0, formValue.healthCardNumber.length - 2)
                    .replaceAll('-', ''),
                versionCode: formValue.healthCardNumber
                    .slice(formValue.healthCardNumber.length - 2)
                    .toUpperCase(),
                controlNumber: formValue.healthCardDocumentControlNumber,
                postalCode: formValue.postalCode,
                dateOfBirth: new Date(
                    formValue.dateOfBirthYear
                        ? parseInt(formValue.dateOfBirthYear?.toString() || '', 10)
                        : 0,
                    formValue.dateOfBirthMonth
                        ? parseInt(formValue.dateOfBirthMonth?.toString() || '', 10) - 1
                        : 0,
                    formValue.dateOfBirthDay
                        ? parseInt(formValue.dateOfBirthDay?.toString() || '', 10)
                        : undefined
                ).toISOString(),
            },
        }),
    } as Products;
};

export const maskString = (
    inputString: string,
    startIndex: number,
    endIndex?: number,
    maskCharacter = '*'
) => {
    if (!endIndex) {
        endIndex = inputString.length;
    }
    return inputString.replace(
        new RegExp(`^(.{${startIndex}}).{${endIndex - startIndex}}`),
        (_, prefix) => prefix + maskCharacter.repeat(endIndex || 0 - startIndex)
    );
};

export const maskLicensePlate = (plateNumber: string) => {
    const startIndex = 0;
    let endIndex = plateNumber.length;
    if (plateNumber.length >= 4) {
        endIndex = plateNumber.length - 2;
    }

    return maskString(plateNumber, startIndex, endIndex);
};

export const maskCardNumber = (cardNumber: string, removeDashesAndWhiteSpaces = false) => {
    if (!cardNumber) {
        return cardNumber;
    }

    const cardNumberToMask = removeDashesAndWhiteSpaces
        ? cardNumber.replaceAll(/[\s-]/g, '')
        : cardNumber;
    return maskString(cardNumberToMask, 0, cardNumberToMask.length - 3);
};

export const maskPhoneNumber = (number: string) => {
    return maskString(number, 0, number.length - 3);
};

export const maskPhoneExtensionNumber = (number: string) => {
    return maskString(number, 0, number.length - 1);
};

export const maskEmailAddress = (emailId: string) => {
    if (!emailId) {
        return emailId;
    }

    const emailParts = emailId?.split('@');
    let maskedEmail = emailParts[0].substring(0, 2) + '******';
    if (emailParts.length > 1) {
        const emailDomainParts = emailParts[1].split('.');
        maskedEmail += `@${emailDomainParts[0].substring(0, 1)}*****`;
        if (emailDomainParts.length > 1) {
            maskedEmail += `.${emailDomainParts[1]}`;
        }
    }

    return maskedEmail;
};
/* eslint-disable prefer-template */
const maskAddressType1 = (
    addressInfo: TPCAddressInfo,
    showAll: boolean,
    language = 'EN'
): string => {
    let unitType = '';
    if (addressInfo?.unitType) {
        unitType =
            UnitTypes(language as Locale).filter(u => u.value === addressInfo?.unitType)[0].text ||
            '';
    }
    const ret =
        (addressInfo?.unitNumber ? `${unitType || ''} ${addressInfo.unitNumber} - ` : '') +
        `${addressInfo.streetNumber || ''}${addressInfo.streetNumberSuffix || ''} ` +
        (addressInfo?.province === 'QC' ? `${addressInfo?.streetType || ''} ` : ``) +
        (showAll
            ? `${addressInfo?.streetName || ''} `
            : `${maskString(
                  addressInfo?.streetName || '',
                  0,
                  addressInfo?.streetName?.length || 0
              )} `) +
        (addressInfo?.province !== 'QC' ? `${addressInfo?.streetType || ''} ` : ``) +
        ` ${addressInfo?.streetDirection || ''}` +
        `<br />` +
        (showAll
            ? `${addressInfo?.municipality} `
            : `${maskString(
                  addressInfo?.municipality || '',
                  0,
                  addressInfo?.municipality?.length || 0
              )} `) +
        `${addressInfo?.province} ` +
        (showAll
            ? `${addressInfo?.postalCode} `
            : `${maskString(addressInfo?.postalCode || '', 0, 3)}`);

    return ret;
};

const maskAddressType2 = (
    addressInfo: TPCAddressInfo,
    showAll: boolean,
    language = 'EN'
): string => {
    let unitType = '';
    if (addressInfo?.unitType) {
        unitType =
            UnitTypes(language as Locale).filter(u => u.value === addressInfo?.unitType)[0].text ||
            '';
    }
    const ret =
        (addressInfo?.unitNumber ? `${unitType} ${addressInfo.unitNumber} - ` : '') +
        `${addressInfo.streetNumber}${addressInfo.streetNumberSuffix || ''} ` +
        (addressInfo?.province === 'QC' ? `${addressInfo?.streetType || ''} ` : ``) +
        (showAll
            ? `${addressInfo?.streetName || ''} `
            : `${maskString(
                  addressInfo?.streetName || '',
                  0,
                  addressInfo?.streetName?.length || 0
              )} `) +
        (addressInfo?.province !== 'QC' ? `${addressInfo?.streetType || ''} ` : ``) +
        ` ${addressInfo?.streetDirection || ''}` +
        `<br />` +
        `${addressInfo?.routeType || ''} ` +
        (showAll
            ? `${addressInfo?.routeNumber || ''} `
            : `${maskString(addressInfo?.routeNumber || '', 0)} `) +
        `<br />` +
        (showAll
            ? `${addressInfo?.municipality} `
            : `${maskString(
                  addressInfo?.municipality || '',
                  0,
                  addressInfo?.municipality?.length || 0
              )} `) +
        `${addressInfo?.province} ` +
        (showAll
            ? `${addressInfo?.postalCode} `
            : `${maskString(addressInfo?.postalCode || '', 0, 3)}`);

    return ret;
};

const maskAddressType3 = (addressInfo: TPCAddressInfo, showAll: boolean): string => {
    const ret =
        `${addressInfo?.poBoxKeyword || ''} ` +
        (showAll
            ? `${addressInfo?.poBoxNumber || ''}`
            : `${maskString(
                  addressInfo?.poBoxNumber || '',
                  2,
                  addressInfo?.poBoxNumber?.length
              )}`) +
        ` ${addressInfo?.deliveryInstallationType || ''} ` +
        (showAll
            ? `${addressInfo?.deliveryInstallationName || ''}`
            : `${maskString(addressInfo?.deliveryInstallationName || '', 0)}`) +
        ` ${addressInfo?.deliveryInstallationArea || ''}` +
        `<br />` +
        (showAll
            ? `${addressInfo?.municipality} `
            : `${maskString(
                  addressInfo?.municipality || '',
                  0,
                  addressInfo?.municipality?.length || 0
              )} `) +
        `${addressInfo?.province} ` +
        (showAll
            ? `${addressInfo?.postalCode} `
            : `${maskString(addressInfo?.postalCode || '', 0, 3)}`);

    return ret;
};

const maskAddressType4 = (addressInfo: TPCAddressInfo, showAll: boolean): string => {
    const ret =
        `${addressInfo?.routeType || ''} ` +
        (showAll
            ? `${addressInfo?.routeNumber || ''}`
            : `${maskString(
                  addressInfo?.routeNumber || '',
                  0,
                  addressInfo?.routeNumber?.length
              )}`) +
        ` ${addressInfo?.deliveryInstallationType || ''} ` +
        (showAll
            ? `${addressInfo?.deliveryInstallationName || ''}`
            : `${maskString(addressInfo?.deliveryInstallationName || '', 0)}`) +
        ` ${addressInfo?.deliveryInstallationArea || ''}` +
        `<br />` +
        (showAll
            ? `${addressInfo?.municipality} `
            : `${maskString(
                  addressInfo?.municipality || '',
                  0,
                  addressInfo?.municipality?.length || 0
              )} `) +
        `${addressInfo?.province} ` +
        (showAll
            ? `${addressInfo?.postalCode} `
            : `${maskString(addressInfo?.postalCode || '', 0, 3)}`);

    return ret;
};

const maskAddressType5 = (addressInfo: TPCAddressInfo, showAll: boolean): string => {
    const ret =
        ` ${addressInfo?.deliveryInstallationType || ''} ` +
        (showAll
            ? `${addressInfo?.deliveryInstallationName || ''}`
            : `${maskString(addressInfo?.deliveryInstallationName || '', 0)}`) +
        ` ${addressInfo?.deliveryInstallationArea || ''}` +
        `<br />` +
        (showAll
            ? `${addressInfo?.municipality} `
            : `${maskString(
                  addressInfo?.municipality || '',
                  0,
                  addressInfo?.municipality?.length || 0
              )} `) +
        `${addressInfo?.province} ` +
        (showAll
            ? `${addressInfo?.postalCode} `
            : `${maskString(addressInfo?.postalCode || '', 0, 3)}`);

    return ret;
};

const maskAddressTypeOthers = (addressInfo: TPCAddressInfo, showAll: boolean): string => {
    if (addressInfo?.address1 || addressInfo?.address2) {
        const addressLine = [addressInfo.address1, addressInfo.address2].filter(Boolean).join(' ');
        return `${showAll ? addressLine : maskString(addressLine, 0, addressLine.length)}<br />${
            showAll
                ? addressInfo?.municipality || ''
                : maskString(
                      addressInfo?.municipality || '',
                      0,
                      addressInfo?.municipality?.length || 0
                  )
        } ${addressInfo?.province || ''} ${
            showAll
                ? addressInfo?.postalCode || ''
                : maskString(addressInfo?.postalCode || '', 0, 3)
        }`;
    }

    const ret =
        `${addressInfo?.suiteNumber ? addressInfo.suiteNumber + ' - ' : ''}` +
        `${addressInfo.streetNumber || ''}${addressInfo.streetNumberSuffix || ''} ${
            showAll
                ? addressInfo?.streetName || ''
                : maskString(addressInfo?.streetName || '', 3, addressInfo?.streetName?.length || 0)
        } ${
            showAll
                ? addressInfo?.municipality || ''
                : maskString(
                      addressInfo?.municipality || '',
                      0,
                      addressInfo?.municipality?.length || 0
                  )
        } ${addressInfo?.province} ${
            showAll
                ? addressInfo?.postalCode || ''
                : maskString(addressInfo?.postalCode || '', 0, 3)
        }`;

    return ret;
};

const maskPhysicalSurveyAddress = (
    addressInfo: TPCAddressInfo,
    showAll: boolean,
    language = 'EN'
): string => {
    const labels = [
        {
            concessionNumber: 'Concession number',
            planNumber: 'Plan number',
        },
        {
            concessionNumber: 'Numéro de concession',
            planNumber: 'Numéro de plan',
        },
    ];
    let lotType = '';
    if (addressInfo?.lotType) {
        lotType =
            LotTypes(language as Locale).filter(l => l.value === addressInfo?.lotType)[0].text ||
            '';
    }
    let unitType = '';
    if (addressInfo?.unitType) {
        unitType =
            UnitTypes(language as Locale).filter(u => u.value === addressInfo?.unitType)[0].text ||
            '';
    }
    let ret =
        `${lotType || ''} ` +
        (showAll
            ? `${addressInfo?.lotNumber || ''}`
            : `${maskString(
                  addressInfo?.lotNumber || '',
                  0,
                  addressInfo?.lotNumber?.length || 0
              )}`);
    if (addressInfo?.concessionNumber) {
        ret += `, ${language === 'EN' ? labels[0].concessionNumber : labels[1].concessionNumber} ${addressInfo?.concessionNumber}`;
    }
    if (addressInfo?.planNumber) {
        ret += `, ${language === 'EN' ? labels[0].planNumber : labels[1].planNumber} ${addressInfo?.planNumber}`;
    }
    ret +=
        `<br /> ` +
        (showAll
            ? `${addressInfo?.township} `
            : `${maskString(
                  addressInfo?.township || '',
                  0,
                  addressInfo?.township?.length || 0
              )} `) +
        `${unitType || ''} ${addressInfo?.unitNumber || ''}` +
        `<br /> ` +
        (showAll
            ? ` ${addressInfo?.municipality} `
            : ` ${maskString(
                  addressInfo?.municipality || '',
                  0,
                  addressInfo?.municipality?.length || 0
              )} `) +
        `${addressInfo?.province} ` +
        (showAll
            ? `${addressInfo?.postalCode} `
            : `${maskString(addressInfo?.postalCode || '', 0, 3)}`);
    return ret;
};

export const maskAddress = (
    addressInfo: TPCAddressInfo,
    showAll = false,
    language = 'EN'
): string => {
    if (!addressInfo || Object.keys(addressInfo).length === 0) {
        return '';
    }
    const addressType = addressInfo?.typeOfAddress || 0;
    if (addressInfo.streetNumberSuffix) {
        addressInfo.streetNumberSuffix = addressInfo.streetNumberSuffix.trim();
    }
    if (addressInfo.noStreetName) {
        return maskPhysicalSurveyAddress(addressInfo, showAll, language);
    }

    return addressType === 1
        ? maskAddressType1(addressInfo, showAll, language)
        : addressType === 2
          ? maskAddressType2(addressInfo, showAll, language)
          : addressType === 3
            ? maskAddressType3(addressInfo, showAll)
            : addressType === 4
              ? maskAddressType4(addressInfo, showAll)
              : addressType === 5
                ? maskAddressType5(addressInfo, showAll)
                : maskAddressTypeOthers(addressInfo, showAll);
};

export const splitHCNumberAndVersionCode = (value: string) => {
    if (!value) {
        return undefined;
    }
    const noFormatValue = value.replaceAll(/[-\s]/g, '');
    const healthCardVersionCode = noFormatValue.match(/[a-zA-Z]{1,2}/g)?.[0] || undefined;
    const healthCardNumber = healthCardVersionCode
        ? value.replace(/[\s-]?[a-zA-Z]{1,2}/, '')
        : value;
    return {
        healthCardNumber,
        healthCardVersionCode,
    };
};

export const determineLicencePlateStatus = (
    inquiryData?: LicencePlateInquiryResponse,
    errorCodes?: string[]
): { status: LicencePlateStatus; amount?: number } => {
    if (Array.isArray(errorCodes) && errorCodes?.length) {
        switch (errorCodes[0]) {
            case 'CXPLPR105':
            case 'CXPLPR106':
                return { status: 'PAY_DRIVER_FINE' };
            case 'CXPLPR107':
            case 'CXPLPR108':
                return { status: 'INVALID_INSURANCE' };
            case 'CXPLPR101':
            case 'CXPLPR103':
                return { status: 'RENEW_IN_PERSON' };
            default:
                return { status: 'NOT_RENEWABLE' };
        }
    }

    const plateRenewal = inquiryData?.items?.item?.plateRenewal;
    if (plateRenewal?.itemStatus?.success) {
        const denials = plateRenewal.termInfo?.[0]?.itemCost?.denials ?? [];
        const totalDenialCost = denials?.reduce(
            (denialCost, currentDenial) => denialCost + (currentDenial?.denialTypeTotalAmount ?? 0),
            0
        );

        if (totalDenialCost <= 0) {
            return { status: 'RENEW_NOW' };
        }
        return { status: 'PAY_AND_RENEW', amount: totalDenialCost };
    }
    return { status: 'NOT_RENEWABLE' };
};

export const mapApiErrorCodeToProductsSection = (
    type: TIdType,
    operation: 'ADD' | 'UPDATE' | 'REMOVE',
    apiErrorCodes?: string[],
    ...others: string[]
): string[] | undefined => {
    if (!apiErrorCodes) {
        return undefined;
    }

    let errorCodes: string[] | undefined;
    switch (type) {
        case 'DriversLicense':
            errorCodes = apiErrorCodes?.map((code: string) =>
                code === 'CXP00000' ? (operation === 'REMOVE' ? 'CXPID022' : 'CXPID020') : code
            );
            break;
        case 'PhotoCard':
            errorCodes = apiErrorCodes?.map((code: string) =>
                code === 'CXP00000' ? (operation === 'REMOVE' ? 'CXPID026' : 'CXPID024') : code
            );
            break;
        case 'LicensePlate':
            errorCodes = apiErrorCodes?.map((code: string) => {
                if (['CXPID054', 'CXPID055', 'CXPID056'].includes(code)) {
                    const licencePlates = (others ?? []).join(',');
                    code = [code, `plates:${licencePlates}`].join(',');
                }

                if (code === 'CXPID056' || code === 'CXPID055') {
                    code += `,linkOfDocuments:"${urlWithBasePath('/profile/products')}"`;
                }
                return code === 'CXP00000'
                    ? operation === 'REMOVE'
                        ? 'CXPID030'
                        : 'CXPID028'
                    : code;
            });
            break;
        case 'HealthCard':
            errorCodes = apiErrorCodes?.map((code: string) =>
                code === 'CXP00000' ? (operation === 'REMOVE' ? 'CXPID034' : 'CXPID032') : code
            );
            break;
        default:
            break;
    }

    return errorCodes;
};

export const showTechnicalIssueAlert = (errors?: string[] | null) => {
    const errorList = errors ?? [];
    return errorList.some(code => profileErrorCodes.includes(code));
};

export const getTechnicalIssueContentKey = (errors?: string[] | null) => {
    const errorList = errors ?? [];
    if (errorList.some(code => ['CXPPD041', 'CXPPD043', 'CXPPD044'].includes(code)))
        return 'productsSection.technicalIssueInfo1';
    return 'productsSection.technicalIssueInfo2'; // 'CXPPD042', 'CXPPD045', ...
};
